import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useTheme } from "../context/ThemeContext";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "./contact.css";

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });

  const [feedbackData, setFeedbackData] = useState({
    rating: 0,
    ratingText: "",
    feedbackCategory: "",
    title: "",
    detailedDescription: "",
    email: "",
    name: "",
    
  });

  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);
  const [showContactSuccess, setShowContactSuccess] = useState(false);
  const [contactErrors, setContactErrors] = useState([]);
  const [feedbackErrors, setFeedbackErrors] = useState([]);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Rating text mapping
  const getRatingText = (rating) => {
    const ratingMap = {
      1: 'Very Poor',
      2: 'Poor', 
      3: 'Average',
      4: 'Good',
      5: 'Excellent'
    };
    return ratingMap[rating] || '';
  };

  // Validation functions
  const validateContactForm = (data) => {
    const errors = [];
    
    if (!data.firstName || data.firstName.trim().length < 2) {
      errors.push('Please enter your first name (at least 2 characters)');
    }
    
    if (!data.lastName || data.lastName.trim().length < 2) {
      errors.push('Please enter your last name (at least 2 characters)');
    }
    
    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('Please enter a valid email address (e.g., yourname@email.com)');
    }
    
    if (!data.message || data.message.trim().length < 10) {
      errors.push('Please tell us more about your message (at least 10 characters)');
    }
    
    return errors;
  };

  const validateFeedbackForm = (data) => {
    const errors = [];
    
    if (!data.rating || data.rating < 1 || data.rating > 5) {
      errors.push('Please select a rating by clicking on the stars above');
    }
    
    if (!data.feedbackCategory || data.feedbackCategory.trim().length === 0) {
      errors.push('Please select a feedback category from the dropdown');
    }
    
    
    
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('Please enter a valid email address if you want us to contact you');
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (contactErrors.length > 0) {
      setContactErrors([]);
    }
  };

  const handleFeedbackChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFeedbackData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user starts typing
    if (feedbackErrors.length > 0) {
      setFeedbackErrors([]);
    }
  };

  const handleRatingClick = (rating) => {
    const ratingText = getRatingText(rating);
    setFeedbackData(prev => ({
      ...prev,
      rating: rating,
      ratingText: ratingText
    }));
    // Clear errors when user selects rating
    if (feedbackErrors.length > 0) {
      setFeedbackErrors([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation first
    const validationErrors = validateContactForm(formData);
    if (validationErrors.length > 0) {
      setContactErrors(validationErrors);
      return;
    }
    
    setIsSubmittingContact(true);
    setContactErrors([]);
    
    try {
      console.log("contact:write:start");
      
      // Generate a new document reference to get a unique ID
      const uid = doc(collection(db, "messages"));
      
      await setDoc(uid, {
        uid: uid.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        createdAt: serverTimestamp()
      });
      
      console.log("contact:write:ok", uid.id);
      
      setShowContactSuccess(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: ""
      });
      // Hide success message after 5 seconds
      setTimeout(() => setShowContactSuccess(false), 5000);
      
    } catch (error) {
      console.error("contact:submit", error);
      setContactErrors(["Sorry, something went wrong. Please try again or contact us directly."]);
    } finally {
      setIsSubmittingContact(false);
    }
  };
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation first
    const validationErrors = validateFeedbackForm(feedbackData);
    if (validationErrors.length > 0) {
      setFeedbackErrors(validationErrors);
      return;
    }
    
    setIsSubmittingFeedback(true);
    setFeedbackErrors([]);
    
    try {
      console.log("feedback:write:start");
      
      // Generate a new document reference to get a unique ID
      const uid = doc(collection(db, "feedback"));
      
      await setDoc(uid, {
        uid: uid.id,
        rating: feedbackData.rating,
        ratingText: feedbackData.ratingText,
        feedbackCategory: feedbackData.feedbackCategory,
        title: feedbackData.title,
        detailedDescription: feedbackData.detailedDescription,
        email: feedbackData.email,
        name: feedbackData.name,
        
        createdAt: serverTimestamp()
      });
      
      console.log("feedback:write:ok", uid.id);
      
      setShowFeedbackSuccess(true);
      setFeedbackData({
        rating: 0,
        ratingText: "",
        feedbackCategory: "",
        title: "",
        detailedDescription: "",
        email: "",
        name: "",
        
      });
      // Hide success message after 5 seconds
      setTimeout(() => setShowFeedbackSuccess(false), 5000);
      
    } catch (error) {
      console.error("feedback:submit", error);
      setFeedbackErrors(["Sorry, something went wrong. Please try again or contact us directly."]);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };
  const feedbackCategories = [
    "General Feedback",
    "Feature Request",
    "Bug Report",
    "User Experience",
    "Performance",
    "Documentation",
    "Other"
  ];

  return (
    <>
      <Navbar />
      <main className={`contact-page ${isDark ? 'contact-dark' : 'contact-light'}`}>
        <Container className="py-5">
          {/* Header Section */}
          <div className="text-center mb-5">
            <div className="contact-badge mb-3">
              <span className="badge bg-primary px-3 py-2 rounded-pill">CONTACT US</span>
            </div>
            <h1 className={`display-4 fw-bold ${isDark ? 'text-white' : 'text-dark'} mb-3`}>
              Get in touch with our team
            </h1>
          </div>

          <Row className="g-5 align-items-start">
            {/* Left Column - Contact Info & Testimonial */}
            <Col lg={6} className="mb-4">
              <div className="contact-info">
                <p className={`fs-5 mb-4 ${isDark ? 'text-white-50' : 'text-muted'}`}>
                  Feel free to reach out - we'd love to connect.
                </p>

                {/* Contact Options */}
                <div className="contact-options mb-5">
                  <div className="contact-option d-flex align-items-center mb-3">
                    <div className={`contact-icon me-3 ${isDark ? 'bg-primary' : 'bg-primary'}`}>
                      <i className="bi bi-envelope text-white"></i>
                    </div>
                    <div>
                      <h6 className={`mb-1 ${isDark ? 'text-white' : 'text-dark'}`}>Email us</h6>
                      <a href="mailto:contact@epcspark.co.uk" className={`text-decoration-none ${isDark ? 'text-white-50' : 'text-muted'}`}>
                        contact@epcspark.co.uk
                      </a>
                    </div>
                  </div>

                  <div className="contact-option d-flex align-items-center">
                    <div className={`contact-icon me-3 ${isDark ? 'bg-primary' : 'bg-primary'}`}>
                      <i className="bi bi-chat-dots text-white"></i>
                    </div>
                    <div>
                      <h6 className={`mb-1 ${isDark ? 'text-white' : 'text-dark'}`}>Get support</h6>
                      <span className={isDark ? 'text-white-50' : 'text-muted'}>Chat with us</span>
                    </div>
                  </div>
                </div>

                {/* Testimonial */}
                <div className={`testimonial p-4 rounded-3 ${isDark ? 'bg-dark' : 'bg-light'}`}>
                  <blockquote className={`mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>
                    "EPC Spark has revolutionized the way we conduct energy assessments. The seamless integration and advanced tools have saved us countless hours and improved our accuracy significantly."
                  </blockquote>
                  <div className="d-flex align-items-center">
                    <div className="testimonial-avatar me-3">
                      <div className={`rounded-circle ${isDark ? 'bg-primary' : 'bg-primary'}`} style={{ width: 48, height: 48 }}>
                        <i className="bi bi-person text-white d-flex align-items-center justify-content-center h-100"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className={`mb-0 ${isDark ? 'text-white' : 'text-dark'}`}>Sarah Johnson</h6>
                      <small className={isDark ? 'text-white-50' : 'text-muted'}>Senior Energy Assessor, GreenTech Solutions</small>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right Column - Contact Form */}
            <Col lg={6}>
              <div className={`contact-form p-4 rounded-3 ${isDark ? 'bg-dark' : 'bg-white'} shadow-sm`}>
                {/* Contact Form Errors */}
                {contactErrors.length > 0 && (
                  <Alert variant="danger" className="mb-3 border-0">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <strong>Please fix the following:</strong>
                    </div>
                    <ul className="mb-0 ps-3">
                      {contactErrors.map((error, index) => (
                        <li key={index} className="mb-1">{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Enter your first name"
                          className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                          required
                          disabled={isSubmittingContact}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Enter your last name"
                          className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                          required
                          disabled={isSubmittingContact}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                      required
                      disabled={isSubmittingContact}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Your phone number (optional)"
                      className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                      disabled={isSubmittingContact}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us how we can help you..."
                      className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                      required
                      disabled={isSubmittingContact}
                    />
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg" 
                    className="w-100 fw-semibold"
                    disabled={isSubmittingContact}
                  >
                    {isSubmittingContact ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </Button>

                  <p className={`text-center mt-3 small ${isDark ? 'text-white-50' : 'text-muted'}`}>
                    By submitting this form you agree to our{" "}
                    <a href="/privacypolicy" className="text-decoration-none">Privacy Policy</a>
                  </p>
                </Form>
              </div>
            </Col>
          </Row>

          {/* Success Messages */}
          {showContactSuccess && (
            <Alert variant="success" className={`mt-4 ${isDark ? 'text-white' : 'text-dark'}`} onClose={() => setShowContactSuccess(false)} dismissible>
              <i className="bi bi-check-circle me-2"></i>
              <strong>Message sent successfully! 🎉</strong> We've received your message and will get back to you soon.
            </Alert>
          )}

          {/* Feedback Section */}
          <Row className="mt-5 pt-5">
            <Col lg={12}>
              <div className="text-center mb-5">
                <div className="feedback-badge mb-3">
                  <span className="badge bg-success px-3 py-2 rounded-pill">FEEDBACK</span>
                </div>
                <h2 className={`display-5 fw-bold ${isDark ? 'text-white' : 'text-dark'} mb-3`}>
                  Share Your Feedback
                </h2>
                <p className={`fs-5 ${isDark ? 'text-white-50' : 'text-muted'}`}>
                  Help us improve EPC Spark by sharing your thoughts, suggestions, and experiences
                </p>
              </div>

              <div className={`feedback-form-container p-5 rounded-4 ${isDark ? 'bg-dark' : 'bg-white'} shadow-lg`}>
                {/* Feedback Form Errors */}
                {feedbackErrors.length > 0 && (
                  <Alert variant="danger" className="mb-3 border-0">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <strong>Almost there! Please fix the following:</strong>
                    </div>
                    <ul className="mb-0 ps-3">
                      {feedbackErrors.map((error, index) => (
                        <li key={index} className="mb-1">{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <Form onSubmit={handleFeedbackSubmit}>
                  {/* Rating Section */}
                  <div className="rating-section text-center mb-4">
                    <h5 className={`mb-3 ${isDark ? 'text-white' : 'text-dark'}`}>How would you rate your experience?</h5>
                    <div className="rating-stars d-flex justify-content-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className={`rating-star ${feedbackData.rating >= star ? 'active' : ''}`}
                          onClick={() => handleRatingClick(star)}
                          disabled={isSubmittingFeedback}
                        >
                          <i className={`bi ${feedbackData.rating >= star ? 'bi-star-fill' : 'bi-star'}`}></i>
                        </button>
                      ))}
                    </div>
                    <small className={`${isDark ? 'text-white-50' : 'text-muted'}`}>
                      {feedbackData.rating === 0 && "Click to rate"}
                      {feedbackData.rating > 0 && `${feedbackData.rating}/5 - ${feedbackData.ratingText}`}
                    </small>
                  </div>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Feedback Category</Form.Label>
                        <Form.Select
                          name="feedbackCategory"
                          value={feedbackData.feedbackCategory}
                          onChange={handleFeedbackChange}
                          className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                          required
                          disabled={isSubmittingFeedback}
                        >
                          <option value="">Select a category</option>
                          {feedbackCategories.map((category) => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Title</Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={feedbackData.title}
                          onChange={handleFeedbackChange}
                          placeholder="Give your feedback a title"
                          className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                          required
                          disabled={isSubmittingFeedback}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Detailed Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="detailedDescription"
                      value={feedbackData.detailedDescription}
                      onChange={handleFeedbackChange}
                      placeholder="Share your thoughts, suggestions, or describe any issues you've encountered..."
                      className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                      required
                      disabled={isSubmittingFeedback}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Your Name (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={feedbackData.name}
                          onChange={handleFeedbackChange}
                          placeholder="Your name (optional)"
                          className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                          disabled={isSubmittingFeedback}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className={`${isDark ? 'text-white' : 'text-dark'}`}>Email (Optional)</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={feedbackData.email}
                          onChange={handleFeedbackChange}
                          placeholder="your.email@example.com (optional)"
                          className={isDark ? 'bg-dark text-white border-secondary' : 'bg-white text-dark'}
                          disabled={isSubmittingFeedback}
                        />
                      </Form.Group>
                    </Col>
                  </Row>


                  <div className="text-center">
                    <Button 
                      type="submit" 
                      variant="success" 
                      size="lg" 
                      className="px-5 fw-semibold"
                      disabled={feedbackData.rating === 0 || isSubmittingFeedback}
                    >
                      {isSubmittingFeedback ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Submit Feedback
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>

          {/* Feedback Success Message */}
          {showFeedbackSuccess && (
            <Alert variant="success" className={`mt-4 ${isDark ? 'text-white' : 'text-dark'}`} onClose={() => setShowFeedbackSuccess(false)} dismissible>
              <i className="bi bi-check-circle me-2"></i>
              <strong>Feedback submitted successfully! 🌟</strong> Thank you for helping us improve EPC Spark. We'll review your feedback carefully.
            </Alert>
          )}
        </Container>
      </main>
      <Footer />
    </>
  );
}
